import React from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, DialogTitle, IconButton } from '@mui/material';
import { DataResultsDto, JsonUtils, ResourceClientService } from '@lib/common-sdk';
import { Button, ButtonColor, Dialog, GeneralForm, GeneralFormItem, GeneralFormRowOfItems, Snackbar } from '../../index';
import CloseIcon from '@mui/icons-material/Close';
import { LargeAddIcon } from '../LargeAddIcon';
import { AddIcon } from '../AddIcon';
import { Breakpoint } from '@mui/system';
import i18n from 'i18next';
import { getErrorMessage } from '../../custom-post-error.handler';

export interface AddModelButtonProps {
  color?: ButtonColor;
  variant?: 'text' | 'outlined' | 'contained';
  onBeforeSave?: (formData: any) => any;
  onSaved: (result: any) => Promise<any>;
  overrideSaveCall?: (payload: any) => Promise<DataResultsDto<any>>;
  /** Collection to POST to, e.g. '/api/resource/wastes'. Omitted when overrideSaveCall does the writing. */
  url?: string;
  testId?: string;
  validationSchema?: any;
  buttonLabel: string;
  dialogTitle: string;
  fields: (GeneralFormItem | GeneralFormRowOfItems)[];
  hiddenValues?: any;
  bottomMessage?: string;
  postErrorMappings?: Record<string, string>;
  multipart?: boolean;
  startIcon?: JSX.Element;
  dialogIcon?: JSX.Element;
  maxWidth?: Breakpoint | false;
}

/** The last URL segment, so a data-testid stays `wastes-add-btn` now that the prop carries a path. */
const resourceName = (url?: string) => (url ?? '').split('?')[0].replace(/\/$/, '').split('/').pop() || '';

export const AddModelButton = (props: AddModelButtonProps) => {
  const { t } = useTranslation(); // needed to react to language change (even if not used directly)
  const translations: any = i18n.getDataByLanguage(i18n.language);
  const errorKeys = translations.error;
  const [failureMessage, setFailureMessage] = React.useState('');
  const [openDialog, setDialog] = React.useState(false);
  // Neither a url nor an overrideSaveCall means there is nothing to POST to - the dialog belongs to a mockup screen.
  // See handleOpenDialog.
  const notImplemented = props.overrideSaveCall == null && props.url == null;

  async function handleSaveDialog(formData: any) {
    let results: DataResultsDto<any>;
    try {
      if (props.onBeforeSave) {
        formData = props.onBeforeSave(formData);
      }
      if (props.overrideSaveCall) {
        results = await props.overrideSaveCall({ ...props.hiddenValues, ...formData });
      } else {
        // handleOpenDialog keeps a dialog with no save path from opening at all, so reaching this without a url
        // would mean the guard was removed rather than that a user got here.
        if (!props.url) {
          throw new Error('AddModelButton needs either a url or an overrideSaveCall');
        }
        results = await ResourceClientService.create<any>(
          props.url,
          { ...props.hiddenValues, ...formData },
          {
            contentType: props?.multipart ? 'multipart/form-data' : 'application/json',
          },
        );
      }
      if (!results.failed) {
        setDialog(false);
        setTimeout(() => props.onSaved(results), 0);
      } else {
        if (props.postErrorMappings !== undefined) {
          const errorMessage = props.postErrorMappings[`${results.errorStatusCode}`];
          if (errorMessage !== undefined) {
            setFailureMessage(errorMessage);
            return;
          }
        }
        setFailureMessage(getErrorMessage(results.response, translations) || t('Common.error'));
      }
    } catch (e: any) {
      const status = e.response?.status;
      const data = e.response?.data || '{ "code": "E00001"}';
      const code = JsonUtils.extractValue(data, 'code');
      const context = JsonUtils.extractValue(data, 'context');
      if (props.postErrorMappings !== undefined) {
        const errorMessage = props.postErrorMappings[`${status}`];
        if (errorMessage !== undefined) {
          setFailureMessage(errorMessage);
          return;
        }
      }
      setFailureMessage(getErrorMessage({ code, context }, translations) || t('Common.error'));
    }
  }

  async function handleOpenDialog() {
    if (notImplemented) {
      // The dialogs left without a save path are the mockup screens. They used to fill in the whole form and then
      // fail on save - a 404 from a resource that never existed, or a thrown Error. Say so on the click instead.
      setFailureMessage(t('featureNotImplemented'));
      return;
    }
    setDialog(true);
  }

  async function handleCloseDialog() {
    setDialog(false);
  }

  return (
    <>
      <Button
        dataTestId={`${props.testId || resourceName(props.url)}-add-btn`}
        label={props.buttonLabel}
        onClick={handleOpenDialog}
        variant={props.variant}
        color={props.color}
        startIcon={props.startIcon ? props.startIcon : <AddIcon />}
      />
      <Dialog open={openDialog} onClose={() => handleCloseDialog()} maxWidth={props.maxWidth === undefined ? 'md' : props.maxWidth} fullWidth={true}>
        <div style={{ margin: 'auto', marginTop: '2rem' }}>{props.dialogIcon ? props.dialogIcon : <LargeAddIcon />}</div>
        <DialogTitle sx={{ margin: 'auto', fontSize: '1.7rem', color: '#183362' }}>
          {props.dialogTitle}
          <IconButton
            aria-label='close'
            data-test-id={'dialog-close-btn'}
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <GeneralForm
            fields={props.fields}
            validationSchema={props.validationSchema}
            onSaved={async (data) => handleSaveDialog(data)}
            onCanceled={async () => handleCloseDialog()}
            hideCancelButton={false}
            hiddenValues={props.hiddenValues}
            variant='standard'
          />
          {props.bottomMessage && (
            <DialogTitle
              sx={{
                margin: 'auto',
                fontSize: '1rem',
                maxWidth: '30rem',
                textAlign: 'center',
              }}
            >
              {props.bottomMessage}
            </DialogTitle>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar message={failureMessage} severity={notImplemented ? 'info' : 'error'} onClose={() => setFailureMessage('')} />
    </>
  );
};
